import { AxiosError } from 'axios';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { Alert, AlertDescription } from '../../../components/ui/alert';
import { Form, FormControl, FormField, FormItem } from '../../../components/ui/form';
import { Input } from '../../../components/ui/input';
import { authService } from '../../../services/auth';
import { generateErrorMessages } from '../../../utils/showApiErrors';
import { ResetPasswordSuccess } from './ResetPasswordSucces';
import { WarningTriangle } from './svg/WarningTriangle';

export const LinkError = ({ title = '', description = '' }: { title: string; description: string }): JSX.Element => {
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | string[]>('');
  const form = useForm({
    defaultValues: {
      email: ''
    }
  });
  const {
    handleSubmit,
    control,
    formState: { errors: emailError, isValid, isDirty }
  } = form;

  const emailInput = useWatch({ control, name: 'email' });

  const onSubmit = async (data: { email: string }): Promise<void> => {
    try {
      if (!isValid || !isDirty) {
        return;
      }

      await authService.requestPasswordReset({ email: data.email });
      setSuccess(true);
    } catch (e) {
      const axiosError = e as AxiosError;
      setError(generateErrorMessages(axiosError));
      setSuccess(false);
    }
  };

  if (success) {
    return <ResetPasswordSuccess email={emailInput} onResendMail={() => setSuccess(false)} />;
  }

  return (
    <Form {...form}>
      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate className="flex flex-col gap-[18px]">
        <div className="flex flex-col gap-[11px]">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
            <WarningTriangle />
          </span>
          <h1 className="font-onboarding-1 text-[2rem] font-bold leading-[1.04] tracking-[-0.025em] text-primary min-[880px]:text-[2.35rem]">
            {title}
          </h1>
          <p id="email-hint" className="text-[15.5px] leading-[1.5] text-[var(--ink-2)]">
            {description}
          </p>
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-[13px] font-medium text-[var(--ink-2)]">Email address</p>
          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    enterKeyHint="send"
                    placeholder="you@yourbrand.com"
                    aria-invalid={form.formState.errors.email ? 'true' : 'false'}
                    aria-describedby={form.formState.errors.email ? 'email-error' : 'email-hint'}
                    {...field}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          {emailError.email?.message && (
            <p id="email-error" aria-live="polite" className="text-[13px] font-medium text-loss">
              {emailError.email.message}
            </p>
          )}
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>
              {Array.isArray(error) ? (
                <div className="space-y-1">
                  {error.map((err, index) => (
                    <div key={index}>{err}</div>
                  ))}
                </div>
              ) : (
                error
              )}
            </AlertDescription>
          </Alert>
        )}

        <button
          type="submit"
          className="btn-cta-orange flex h-12 w-full items-center justify-center rounded-lg text-[15px] font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          Request new link
        </button>
      </form>
    </Form>
  );
};
